import React, { useMemo, useCallback, useState, MutableRefObject } from "react";
import { useTranslation } from "react-i18next";
import { debounce } from "../../util/debounce";
import { IconSearch } from "../../icons/IconSearch/IconSearch";
import { IconClose } from "../../icons/IconClose/IconClose";
import "./LeafSearchInput.scss";
import defaultContent from "../../../content/leafFilter";

export type Props = {
  placeholder: string;
  callback: Function;
  clearOnClick: Function;
  debounceMS: number;
  minDigit?: number;
  minChar?: number;
  searchKeyword: string;
  disabled?: boolean;
  inputRef?: MutableRefObject<HTMLInputElement | null>;
  hideInputArrows?: boolean;
};

const LeafSearchInput = (props: Props) => {
  const { t } = useTranslation(["leafFilter"]);
  const {
    placeholder,
    callback,
    clearOnClick,
    debounceMS,
    minDigit = 0,
    minChar = 0,
    searchKeyword = "",
    disabled = false,
    inputRef,
    hideInputArrows,
  } = props;
  const [focused, setFocused] = useState(false);
  const [inputText, setInputText] = useState(searchKeyword);

  const cancelOnClick = (e: React.MouseEvent<HTMLSpanElement, MouseEvent>): void => {
    setInputText("");
    setFocused(false);
  };

  const _clearOnClick = (e: React.MouseEvent<HTMLSpanElement, MouseEvent>): void => {
    setInputText("");
    clearOnClick();
  };

  const onFocus = (e: React.FocusEvent<HTMLSpanElement>) => {
    setFocused(true);
  };

  const onBlur = (e: React.FocusEvent<HTMLSpanElement>) => {
    if (e.relatedTarget === null) {
      setFocused(false);
    }
  };

  const debouncedSearch = useMemo(
    () =>
      debounce((keyword: string) => {
        const isPureNumberic = keyword && isNaN(Number(keyword)) === false;
        const matchDigitLimit = keyword.length >= minDigit;
        const matchCharLimit = keyword.length >= minChar;
        const matchMinLimit = isPureNumberic === true ? matchDigitLimit : matchCharLimit;

        if (matchMinLimit === true) {
          callback(keyword);
        }

        if (keyword.length === 0) {
          clearOnClick();
        }
      }, debounceMS),
    [callback, clearOnClick, debounceMS, minChar, minDigit],
  );

  const changeHandler = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>): void => {
      const inputText = e.target.value;
      setInputText(inputText);
      if (inputText) {
        debouncedSearch(inputText);
      } else {
        callback(inputText);
      }
    },
    [debouncedSearch],
  );

  return (
    <div className={`search-container ${focused && "search-container--focused"}`}>
      <div className="search-container__input-icons">
        <div className={`search-container__icon ${disabled ? "search-container__icon--disabled" : ""}`}>
          {inputText !== "" ? (
            <span
              data-testid="search-container__button-close"
              className="button-close"
              role="button"
              tabIndex={0}
              aria-label={t("clearSearch", defaultContent["clearSearch"])}
              onClick={_clearOnClick}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setInputText("");
                  clearOnClick();
                }
              }}
            >
              <IconClose height="1.25rem" width="1.25rem" />
            </span>
          ) : (
            <span className="search-container__search-icon">
              <IconSearch height="1.25rem" width="1.25rem" />
            </span>
          )}
        </div>
        <span className={hideInputArrows ? "hideInputArrows" : ""}>
          <input
            type={minChar && minChar > 0 ? "text" : "number"}
            value={inputText}
            onFocus={onFocus}
            onBlur={onBlur}
            placeholder={placeholder}
            onChange={changeHandler}
            disabled={disabled}
            ref={inputRef}
            aria-label={placeholder}
          />
        </span>
      </div>
      {focused === true && (
        <span
          tabIndex={0}
          role="button"
          className="search-container__cancel-button"
          onClick={cancelOnClick}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setInputText("");
              setFocused(false);
            }
          }}
        >
          {t("cancel", defaultContent["cancel"])}
        </span>
      )}
    </div>
  );
};

export default LeafSearchInput;
